import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Sidebar from "../components/Sidebar";
import LanguageSelector from "../components/LanguageSelector";
import FormattedNumberInput from "../components/FormattedNumberInput";
import ItemSmartFields from "../components/ItemSmartFields";
import { formatMoney, formatNumber } from "../utils/formatCurrency";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { LayoutTemplate, ChevronDown, Trash2, BookmarkPlus, X, Download, ImagePlus, FileText, Search } from "lucide-react";
import { getTranslation } from "../i18n/translations";

const ProposalEditor = ({ user, logout, lang = 'tr', setLang }) => {
  const t = (key) => getTranslation(lang, key);
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [itemSearch, setItemSearch] = useState(""); // teklif içi malzeme/işçilik hızlı arama
  const [attachments, setAttachments] = useState([]);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  // Yeni eklenen kalem id'si — DOM'a ekleninde otomatik scroll için (kullanıcı mouse ile
  // sayfa sonuna kaydırmak zorunda kalmasın)
  const pendingScrollIdRef = useRef(null);

  // Form state
  const [title, setTitle] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyLogo, setCompanyLogo] = useState("");
  
  const [materials, setMaterials] = useState([]);
  const [labor, setLabor] = useState([]);
  const [kdvRate, setKdvRate] = useState(0);
  const [commissionRate, setCommissionRate] = useState(0);
  const [discountRate, setDiscountRate] = useState(0);
  // Gizli kar marjı (%) — ayarlardan gelir, birim fiyatlara yedirilir, müşteri görmez
  const [profitMargin, setProfitMargin] = useState(0);
  const pmFactor = profitMargin > 0 ? 1 + profitMargin / 100 : 1;
  
  // Terms & Bank Info
  const [paymentTerms, setPaymentTerms] = useState("");
  const [validityPeriod, setValidityPeriod] = useState("");
  const [notes, setNotes] = useState("");
  const [bankName, setBankName] = useState("");
  const [iban, setIban] = useState("");
  const [accountHolder, setAccountHolder] = useState("");
  const [status, setStatus] = useState("pending");

  // Hazır teklif şablonları
  const [templates, setTemplates] = useState({ builtin: [], custom: [] });
  const [tplDialogOpen, setTplDialogOpen] = useState(false);
  const [tplName, setTplName] = useState("");
  const [tplSaving, setTplSaving] = useState(false);

  // Fetch customers on mount
  useEffect(() => {
    fetchCustomers();
    fetchTemplates();
    api.get('/user/settings').then((res) => {
      const pm = parseFloat(res?.data?.profit_margin);
      if (!isNaN(pm) && pm > 0) setProfitMargin(pm);
    }).catch(() => {});
  }, []);

  // Marjlı satır toplamı — her zaman canlı hesaplanır (state sırası/yarış durumlarından etkilenmez)
  const lineTotal = (it) =>
    (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0) * pmFactor;

  useEffect(() => {
    if (isEdit) {
      fetchProposal();
    }
  }, [id]);

  // Yeni bir malzeme/işçilik eklendiğinde DOM'a girdiği anda otomatik olarak kartın
  // üstüne kaydırır ve ilk metin girişine odaklanır — kullanıcı elle scroll etmesin.
  useEffect(() => {
    const targetId = pendingScrollIdRef.current;
    if (!targetId) return;
    pendingScrollIdRef.current = null;
    // Bir sonraki paint sonrası tetiklensin ki eleman DOM'da hazır olsun.
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-item-id="${targetId}"]`);
      if (!el) return;
      try {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (e) {
        el.scrollIntoView();
      }
      // Kısa bir gecikmeyle ilk input alanına odaklanır (kullanıcı doğrudan yazmaya başlasın)
      setTimeout(() => {
        const firstInput = el.querySelector('input:not([type="hidden"]):not([type="file"]):not([disabled])');
        if (firstInput && typeof firstInput.focus === 'function') {
          firstInput.focus();
        }
      }, 400);
    }, 60);
    return () => clearTimeout(timer);
  }, [materials.length, labor.length]);

  const fetchCustomers = async () => {
    try {
      const [custRes, propRes] = await Promise.all([
        api.get('/customers').catch(() => ({ data: [] })),
        api.get('/proposals'),
      ]);

      const customerMap = new Map();
      // Önce müşteri veritabanındaki kayıtlar
      custRes.data.forEach((c) => {
        const key = (c.email || c.name || '').toLowerCase();
        if (!key) return;
        customerMap.set(key, {
          id: c.id,
          name: c.name,
          email: c.email || '',
          phone: c.phone || '',
          address: c.address || '',
          company: c.company || '',
        });
      });
      // Tekliflerde geçen ama veritabanında olmayan müşteriler
      propRes.data.forEach(proposal => {
        if (!proposal.customer_name) return;
        const key = (proposal.customer_email || proposal.customer_name).toLowerCase();
        if (!customerMap.has(key)) {
          customerMap.set(key, {
            id: key,
            name: proposal.customer_name,
            email: proposal.customer_email || '',
            phone: proposal.customer_phone || '',
            address: proposal.customer_address || '',
            company: proposal.company_name || '',
          });
        }
      });

      setCustomers(Array.from(customerMap.values()));
    } catch (error) {
      console.error('Müşteriler yüklenemedi:', error);
    }
  };

  const handleCustomerSelect = (customerId) => {
    setSelectedCustomerId(customerId);
    
    if (customerId === "new") {
      // Clear fields for new customer
      setCustomerName("");
      setCustomerPhone("");
      setCustomerEmail("");
      setCustomerAddress("");
      return;
    }
    
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      setCustomerName(customer.name || "");
      setCustomerPhone(customer.phone || "");
      setCustomerEmail(customer.email || "");
      setCustomerAddress(customer.address || "");
      if (customer.company) {
        setCompanyName(customer.company);
      }
    }
  };

  const fetchProposal = async () => {
    try {
      const response = await api.get(`/proposals/${id}`);
      const data = response.data;
      setTitle(data.title);
      setCustomerName(data.customer_name);
      setCustomerPhone(data.customer_phone || "");
      setCustomerEmail(data.customer_email || "");
      setCustomerAddress(data.customer_address || "");
      setCompanyName(data.company_name || "");
      setCompanyLogo(data.company_logo || "");
      setMaterials(data.materials || []);
      setLabor(data.labor || []);
      setAttachments(data.attachments || []);
      setKdvRate(data.kdv_rate || 0);
      setCommissionRate(data.commission_rate || 0);
      setDiscountRate(data.discount_rate || 0);
      setPaymentTerms(data.payment_terms || "");
      setValidityPeriod(data.validity_period || "");
      setNotes(data.notes || "");
      setBankName(data.bank_name || "");
      setIban(data.iban || "");
      setAccountHolder(data.account_holder || "");
      setStatus(data.status || "pending");
    } catch (error) {
      toast.error(t('proposalLoadFailed'));
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!title || !customerName) {
      toast.error(t('enterTitleCustomer'));
      return;
    }

    setSaving(true);
    try {
      // Kar marjını her kalemin toplamına yedir (birim fiyat ham kalır)
      const applyMargin = (arr) => arr.map((it) => {
        const qty = parseFloat(it.quantity) || 0;
        const price = parseFloat(it.unit_price) || 0;
        return { ...it, total: qty * price * pmFactor };
      });
      const data = {
        title,
        customer_name: customerName,
        customer_phone: customerPhone,
        customer_email: customerEmail,
        customer_address: customerAddress,
        company_name: companyName,
        company_logo: companyLogo,
        materials: applyMargin(materials),
        labor: applyMargin(labor),
        attachments,
        kdv_rate: kdvRate,
        commission_rate: commissionRate,
        discount_rate: discountRate,
        profit_margin: profitMargin,
        payment_terms: paymentTerms,
        validity_period: validityPeriod,
        notes: notes,
        bank_name: bankName,
        iban: iban,
        account_holder: accountHolder,
        status: status
      };

      if (isEdit) {
        await api.put(`/proposals/${id}`, data);
        toast.success(t('proposalUpdated'));
        return id;
      } else {
        const res = await api.post("/proposals", data);
        toast.success(t('proposalCreated'));
        // Sayfada kal; tekrar Kaydet'e basılırsa kopya oluşmasın diye düzenleme moduna geç
        navigate(`/proposal/${res.data.id}`, { replace: true });
        return res.data.id;
      }
    } catch (error) {
      toast.error("Teklif kaydedilemedi");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndDownload = async () => {
    const savedId = await handleSave();
    if (!savedId) return;
    setPdfDownloading(true);
    try {
      const response = await api.get(`/proposals/${savedId}/pdf?lang=${lang}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${title || 'teklif'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF indirildi");
    } catch (error) {
      toast.error("PDF indirilemedi");
    } finally {
      setPdfDownloading(false);
    }
  };

  // WhatsApp ile paylaş — teklifi kaydedip public PDF linkini önceden doldurulmuş bir
  // WhatsApp mesajıyla açar. Kullanıcının telefonu varsa doğrudan onun sohbetine gider,
  // yoksa WhatsApp genel gönderim ekranı açılır (mesajı herhangi bir kişiye iletebilir).
  const handleShareWhatsApp = async () => {
    const savedId = await handleSave();
    if (!savedId) return;

    // Backend public PDF URL'ini bul — REACT_APP_BACKEND_URL üzerinden dış URL
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const pdfUrl = `${backendUrl}/api/public/proposals/${savedId}/pdf?lang=${lang}`;

    // Müşteri telefonu — sayı dışı karakterleri temizle
    const phoneDigits = (customerPhone || '').replace(/\D+/g, '');
    // Türkiye başında 0 varsa kaldır (WhatsApp ülke kodu bekler); yoksa dokunma
    let phone = phoneDigits;
    if (phone.startsWith('00')) phone = phone.slice(2);
    if (phone.length === 10 && !phone.startsWith('90') && lang === 'tr') phone = `90${phone}`;
    if (phone.length === 11 && phone.startsWith('0')) phone = `90${phone.slice(1)}`;

    const firstName = (customerName || '').split(/\s+/)[0] || '';
    const greeting = firstName ? `Merhaba ${firstName},` : 'Merhaba,';
    const bodyLines = [
      greeting,
      '',
      `"${title || 'Teklif'}" başlıklı teklifiniz hazırdır.`,
      'PDF görüntülemek için aşağıdaki bağlantıya tıklayabilirsiniz:',
      pdfUrl,
      '',
      companyName ? `— ${companyName}` : '',
    ].filter(Boolean);
    const text = encodeURIComponent(bodyLines.join('\n'));

    const waUrl = phone
      ? `https://wa.me/${phone}?text=${text}`
      : `https://wa.me/?text=${text}`;

    // Yeni sekmede aç
    window.open(waUrl, '_blank', 'noopener');
    toast.success('WhatsApp açılıyor…');
  };

  const addMaterial = () => {
    // Yeni malzeme, önceki malzeme'nin oda/bölümünü otomatik miras alır —
    // aynı odaya arka arkaya kalem eklerken kullanıcı her seferinde yazmasın.
    const lastRoom = materials.length ? (materials[materials.length - 1].room || "") : "";
    const newId = `m_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    pendingScrollIdRef.current = newId;
    setMaterials([...materials, {
      id: newId,
      name: "",
      description: "",
      category: "",
      room: lastRoom,
      quantity: 0,
      unit: "m²",
      unit_price: 0,
      total: 0,
      image_url: "",
      images: []
    }]);
  };

  const removeMaterial = (index) => {
    setMaterials(materials.filter((_, i) => i !== index));
  };

  const updateMaterial = (index, field, value) => {
    const updated = [...materials];
    updated[index][field] = value;
    
    if (field === 'quantity' || field === 'unit_price') {
      const qty = parseFloat(updated[index].quantity) || 0;
      const price = parseFloat(updated[index].unit_price) || 0;
      updated[index].total = qty * price * pmFactor;
    }
    
    setMaterials(updated);
  };

  const addLabor = () => {
    const lastRoom = labor.length ? (labor[labor.length - 1].room || "") : "";
    const newId = `l_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    pendingScrollIdRef.current = newId;
    setLabor([...labor, {
      id: newId,
      name: "",
      description: "",
      category: "",
      room: lastRoom,
      quantity: 0,
      unit: "m²",
      unit_price: 0,
      total: 0,
      image_url: "",
      images: []
    }]);
  };

  const removeLabor = (index) => {
    setLabor(labor.filter((_, i) => i !== index));
  };

  const updateLabor = (index, field, value) => {
    const updated = [...labor];
    updated[index][field] = value;
    
    if (field === 'quantity' || field === 'unit_price') {
      const qty = parseFloat(updated[index].quantity) || 0;
      const price = parseFloat(updated[index].unit_price) || 0;
      updated[index].total = qty * price * pmFactor;
    }
    
    setLabor(updated);
  };

  // Birden fazla alanı tek seferde günceller, toplamı yeniden hesaplar (smart fields için)
  const patchMaterial = (index, patch) => {
    setMaterials((prev) => {
      const updated = [...prev];
      const it = { ...updated[index], ...patch };
      const qty = parseFloat(it.quantity) || 0;
      const price = parseFloat(it.unit_price) || 0;
      it.total = qty * price * pmFactor;
      updated[index] = it;
      return updated;
    });
  };

  const patchLabor = (index, patch) => {
    setLabor((prev) => {
      const updated = [...prev];
      const it = { ...updated[index], ...patch };
      const qty = parseFloat(it.quantity) || 0;
      const price = parseFloat(it.unit_price) || 0;
      it.total = qty * price * pmFactor;
      updated[index] = it;
      return updated;
    });
  };

  // ===== Hazır teklif şablonları =====
  const fetchTemplates = async () => {
    try {
      const res = await api.get("/templates");
      setTemplates({
        builtin: res.data.builtin || [],
        custom: res.data.custom || [],
      });
    } catch (error) {
      console.error("Şablonlar yüklenemedi:", error);
    }
  };

  const applyTemplate = (tpl) => {
    const reId = (arr) =>
      (arr || []).map((it, i) => ({
        ...it,
        id: `${Date.now()}-${i}-${Math.random().toString(36).slice(2, 7)}`,
        images: it.images || [],
      }));
    if (tpl.materials?.length) setMaterials((prev) => [...prev, ...reId(tpl.materials)]);
    if (tpl.labor?.length) setLabor((prev) => [...prev, ...reId(tpl.labor)]);
    if (!title && tpl.name) setTitle(tpl.name);
    toast.success(t('templateAdded').replace('{name}', tpl.name));
  };

  const saveTemplate = async () => {
    if (!tplName.trim()) {
      toast.error(t('templateNameRequired'));
      return;
    }
    if (materials.length === 0 && labor.length === 0) {
      toast.error(t('noItemsForTemplate'));
      return;
    }
    setTplSaving(true);
    try {
      await api.post("/templates", { name: tplName.trim(), materials, labor });
      toast.success(t('templateSaved'));
      setTplDialogOpen(false);
      setTplName("");
      fetchTemplates();
    } catch (error) {
      toast.error(t('templateSaveFailed'));
    } finally {
      setTplSaving(false);
    }
  };

  const deleteTemplate = async (e, tplId) => {
    e.stopPropagation();
    try {
      await api.delete(`/templates/${tplId}`);
      toast.success(t('templateDeleted'));
      fetchTemplates();
    } catch (error) {
      toast.error(t('templateDeleteFailed'));
    }
  };

  const handleImageUpload = async (e, type, index) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    try {
      // Upload multiple files
      const uploadPromises = Array.from(files).map(async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        const response = await api.post("/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        return response.data.url;
      });

      const uploadedUrls = await Promise.all(uploadPromises);

      if (type === "material") {
        const updated = [...materials];
        updated[index].images = [...(updated[index].images || []), ...uploadedUrls];
        setMaterials(updated);
      } else if (type === "labor") {
        const updated = [...labor];
        updated[index].images = [...(updated[index].images || []), ...uploadedUrls];
        setLabor(updated);
      } else if (type === "company_logo") {
        setCompanyLogo(uploadedUrls[0]);
      }
      
      toast.success(t('imagesUploaded').replace('{n}', uploadedUrls.length));
    } catch (error) {
      toast.error(t('imageUploadFailed'));
    }
  };

  const removeImage = (type, itemIndex, imgIndex) => {
    if (type === "material") {
      const updated = [...materials];
      updated[itemIndex].images = updated[itemIndex].images.filter((_, i) => i !== imgIndex);
      setMaterials(updated);
    } else if (type === "labor") {
      const updated = [...labor];
      updated[itemIndex].images = updated[itemIndex].images.filter((_, i) => i !== imgIndex);
      setLabor(updated);
    }
    toast.success("Resim silindi");
  };

  const handleAttachmentUpload = async (e) => {
    const inputEl = e.target;
    const files = inputEl.files;
    if (!files || files.length === 0) return;
    setAttachmentUploading(true);
    try {
      const uploaded = await Promise.all(Array.from(files).map(async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        const res = await api.post("/upload-attachment", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        return { ...res.data, description: "" };
      }));
      setAttachments((prev) => [...prev, ...uploaded]);
      toast.success(`${uploaded.length} dosya eklendi`);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('fileUploadFailed'));
    } finally {
      setAttachmentUploading(false);
      inputEl.value = "";
    }
  };

  const updateAttachment = (index, field, value) => {
    const updated = [...attachments];
    updated[index][field] = value;
    setAttachments(updated);
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
    toast.success(t('fileRemoved'));
  };

  const materialTotal = materials.reduce((sum, item) => sum + lineTotal(item), 0);
  const laborTotal = labor.reduce((sum, item) => sum + lineTotal(item), 0);
  const subtotal = materialTotal + laborTotal;
  const kdvAmount = subtotal * (kdvRate / 100);
  const commissionAmount = subtotal * (commissionRate / 100);
  const discountAmount = subtotal * (discountRate / 100);
  const grandTotal = subtotal + kdvAmount + commissionAmount - discountAmount;

  // Teklif içi hızlı arama — malzeme/işçilik kartlarını isim/açıklama/kategori/oda üzerinden filtreler.
  // Türkçe case-insensitive ("İ"/"ı" için toLocaleLowerCase('tr')).
  const _normSearch = (s) => (s || '').toLocaleLowerCase('tr');
  const _q = _normSearch(itemSearch.trim());
  const matchesSearch = (item) => {
    if (!_q) return true;
    return (
      _normSearch(item.name).includes(_q) ||
      _normSearch(item.description).includes(_q) ||
      _normSearch(item.category).includes(_q) ||
      _normSearch(item.room).includes(_q)
    );
  };
  const roomHasVisible = (list, roomName) => {
    const r = (roomName || '').trim();
    return list.some((m) => ((m.room || '').trim() === r) && matchesSearch(m));
  };
  const anyMatchInList = (list) => !_q || list.some((m) => matchesSearch(m));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <Sidebar user={user} lang={lang} />
      
      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900">
                  {isEdit ? t('editProposal') : t('newProposal')}
                </h1>
                <p className="text-slate-500 text-xs sm:text-sm">
                  {isEdit ? t('editProposalDesc') : t('newProposalDesc')}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <LanguageSelector currentLang={lang} onLanguageChange={setLang} />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-teal-300 text-teal-700 hover:bg-teal-50 text-xs sm:text-sm"
                      data-testid="ready-template-button"
                    >
                      <LayoutTemplate className="w-4 h-4 mr-1.5" />
                      {t('readyTemplate')}
                      <ChevronDown className="w-3.5 h-3.5 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-72 max-h-96 overflow-y-auto">
                    <DropdownMenuLabel>{t('readyTemplates')}</DropdownMenuLabel>
                    {templates.builtin.map((tpl) => (
                      <DropdownMenuItem
                        key={tpl.id}
                        onClick={() => applyTemplate(tpl)}
                        className="cursor-pointer"
                        data-testid={`template-item-${tpl.id}`}
                      >
                        <div className="flex flex-col">
                          <span className="font-medium">{tpl.name}</span>
                          <span className="text-xs text-slate-500">
                            {tpl.materials?.length || 0} {t('materialsLowerWord')} · {tpl.labor?.length || 0} {t('laborLowerWord')}
                          </span>
                        </div>
                      </DropdownMenuItem>
                    ))}
                    {templates.custom.length > 0 && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel>{t('myTemplates')}</DropdownMenuLabel>
                        {templates.custom.map((tpl) => (
                          <DropdownMenuItem
                            key={tpl.id}
                            onClick={() => applyTemplate(tpl)}
                            className="cursor-pointer justify-between"
                            data-testid={`custom-template-${tpl.id}`}
                          >
                            <div className="flex flex-col">
                              <span className="font-medium">{tpl.name}</span>
                              <span className="text-xs text-slate-500">
                                {tpl.materials?.length || 0} {t('materialsLowerWord')} · {tpl.labor?.length || 0} {t('laborLowerWord')}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={(e) => deleteTemplate(e, tpl.id)}
                              className="text-red-500 hover:text-red-700 p-1 ml-2"
                              data-testid={`delete-template-${tpl.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </DropdownMenuItem>
                        ))}
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setTplDialogOpen(true)}
                      className="cursor-pointer text-teal-700 font-medium"
                      data-testid="save-as-template-menu"
                    >
                      <BookmarkPlus className="w-4 h-4 mr-2" />
                      {t('saveAsTemplateMenu')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  onClick={handleSave}
                  disabled={saving || pdfDownloading}
                  size="sm"
                  className="bg-amber-500 hover:bg-amber-600 text-white text-xs sm:text-sm"
                  data-testid="save-proposal-button"
                >
                  {saving ? t('saving') : t('save')}
                </Button>
                <Button
                  onClick={handleSaveAndDownload}
                  disabled={saving || pdfDownloading}
                  size="sm"
                  variant="outline"
                  className="border-amber-500 text-amber-600 hover:bg-amber-50 text-xs sm:text-sm"
                  data-testid="save-and-download-pdf-button"
                >
                  <Download className="w-4 h-4 mr-1" />
                  {pdfDownloading ? t('downloadingBtn') : (<><span className="hidden md:inline">{t('save')} +&nbsp;</span>PDF</>)}
                </Button>
                <Button
                  onClick={handleShareWhatsApp}
                  disabled={saving || pdfDownloading}
                  size="sm"
                  variant="outline"
                  className="border-emerald-500 text-emerald-600 hover:bg-emerald-50 text-xs sm:text-sm"
                  title={t('shareWhatsAppTitle')}
                  data-testid="share-whatsapp-button"
                >
                  {/* WhatsApp SVG (Simple Icons) */}
                  <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                  </svg>
                  <span className="hidden md:inline">{t('shareWhatsAppLabel')}</span>
                  <span className="md:hidden">WA</span>
                </Button>
                <Button
                  onClick={() => navigate("/dashboard")}
                  size="sm"
                  variant="outline"
                  className="border-slate-300 text-slate-600 hover:bg-slate-100 px-2"
                  title={t('backToHomeTitle')}
                  data-testid="close-editor-button"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Teklif İçi Hızlı Arama — malzeme/işçilik kartlarını filtreler */}
            <div className="mt-3 relative max-w-xl">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <Input
                type="text"
                value={itemSearch}
                onChange={(e) => setItemSearch(e.target.value)}
                placeholder={t('inProposalSearchPlaceholder')}
                className="ps-9 pe-9 rounded-full bg-slate-50 border-slate-200 focus:border-teal-400 focus:ring-teal-200 text-sm"
                data-testid="in-proposal-search-input"
              />
              {itemSearch && (
                <button
                  onClick={() => setItemSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-200 rounded-full"
                  data-testid="in-proposal-search-clear"
                >
                  <X className="w-3.5 h-3.5 text-slate-500" />
                </button>
              )}
            </div>
          </div>
        </header>

        {/* Editor Content */}
        <main className="p-4 sm:p-6">
          <div className="max-w-5xl mx-auto space-y-4 sm:space-y-6">
          {/* Genel Bilgiler — restructured per user mockup: white bg, 3 sub-cards */}
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-slate-900 px-1">{t('generalInfo')}</h2>

            <div className="grid grid-cols-1 gap-4">
              {/* Proje ve Teklif Bilgileri (full width) */}
              <Card className="bg-white border-2 border-teal-200 shadow-sm">
                <CardHeader className="px-4 py-3 border-b border-slate-100">
                  <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                    <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {t('projectProposalInfo')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 px-4 py-4">
                  <div>
                    <Label htmlFor="title" className="text-sm">
                      {t('projectTitle')} <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="title"
                      data-testid="project-title-input"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder={t('projectTitlePlaceholder')}
                      className="rounded-lg border-slate-300 focus:border-teal-500 bg-white"
                    />
                  </div>
                  <div>
                    <Label htmlFor="status" className="text-sm">{t('proposalStatusLabel')}</Label>
                    <Select value={status} onValueChange={setStatus}>
                      <SelectTrigger className="rounded-lg border-slate-300 focus:border-teal-500 bg-white" data-testid="status-select">
                        <SelectValue placeholder={t('selectStatusPlaceholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-slate-400"></span>
                            {t('draft')}
                          </span>
                        </SelectItem>
                        <SelectItem value="pending">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                            {t('pending')}
                          </span>
                        </SelectItem>
                        <SelectItem value="sent">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                            {t('sent')}
                          </span>
                        </SelectItem>
                        <SelectItem value="approved">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-green-500"></span>
                            {t('statusApproved')}
                          </span>
                        </SelectItem>
                        <SelectItem value="rejected">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-red-500"></span>
                            {t('rejected')}
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Müşteri ve İletişim Bilgileri (full width) */}
            <Card className="bg-white border-2 border-slate-200 shadow-sm">
              <CardHeader className="px-4 py-3 border-b border-slate-100">
                <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                  <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  {t('customerContactInfo')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 px-4 py-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label htmlFor="customer-select" className="text-sm">{t('selectCustomerLabel')}</Label>
                    <Select value={selectedCustomerId} onValueChange={handleCustomerSelect}>
                      <SelectTrigger className="rounded-lg border-slate-300 focus:border-blue-500 bg-white" data-testid="customer-select">
                        <SelectValue placeholder={t('selectCustomerPlaceholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">
                          <span className="flex items-center gap-2 text-amber-600">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            {t('addNewCustomer')}
                          </span>
                        </SelectItem>
                        {customers.map((customer) => (
                          <SelectItem key={customer.id} value={customer.id}>
                            <span className="flex items-center gap-2">
                              <span className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-medium">
                                {customer.name?.charAt(0)?.toUpperCase() || 'M'}
                              </span>
                              <span>{customer.name}</span>
                              {customer.company && (
                                <span className="text-slate-400 text-xs">({customer.company})</span>
                              )}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="customer" className="text-sm">
                      {t('customerName')} <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="customer"
                      data-testid="customer-name-input"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      placeholder={t('customerNamePlaceholder')}
                      className="rounded-lg border-slate-300 focus:border-blue-500 bg-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                  <div>
                    <Label htmlFor="phone" className="text-sm">{t('phone')}</Label>
                    <Input
                      id="phone"
                      value={customerPhone}
                      onChange={(e) => setCustomerPhone(e.target.value)}
                      placeholder="+90 555 123 45 67"
                      className="rounded-lg border-slate-300 focus:border-blue-500 bg-white"
                    />
                    {customerPhone && (
                      <div className="flex items-center gap-3 mt-1">
                        <a
                          href={`tel:${customerPhone.replace(/\s/g, '')}`}
                          className="text-xs text-teal-600 hover:text-teal-700 inline-flex items-center gap-1"
                          data-testid="call-customer"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          {t('callLabel')}
                        </a>
                        <a
                          href={`https://wa.me/${customerPhone.replace(/\s/g, '').replace(/^\+/, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-green-600 hover:text-green-700 inline-flex items-center gap-1"
                          data-testid="whatsapp-customer"
                        >
                          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                          </svg>
                          WhatsApp
                        </a>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-sm">{t('email')}</Label>
                    <Input
                      id="email"
                      type="email"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      placeholder="musteri@example.com"
                      className="rounded-lg border-slate-300 focus:border-blue-500 bg-white"
                    />
                    {customerEmail && (
                      <a
                        href={`mailto:${customerEmail}`}
                        className="text-xs text-teal-600 hover:text-teal-700 mt-1 inline-flex items-center gap-1"
                        data-testid="email-customer"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {t('sendEmailTitle')}
                      </a>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="address" className="text-sm">{t('address')}</Label>
                    <Input
                      id="address"
                      value={customerAddress}
                      onChange={(e) => setCustomerAddress(e.target.value)}
                      placeholder={t('customerAddressPlaceholder')}
                      className="rounded-lg border-slate-300 focus:border-blue-500 bg-white"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Materials & Labor Tabs */}
          <Tabs defaultValue="materials" className="space-y-4">
            <TabsList className="bg-white p-1 rounded-xl shadow-sm border border-slate-200">
              <TabsTrigger value="materials" className="rounded-lg data-[state=active]:bg-teal-600 data-[state=active]:text-white" data-testid="materials-tab">
                {t('materialCostTab')}
              </TabsTrigger>
              <TabsTrigger value="labor" className="rounded-lg data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="labor-tab">
                {t('laborCostTab')}
              </TabsTrigger>
              <TabsTrigger value="attachments" className="rounded-lg data-[state=active]:bg-purple-600 data-[state=active]:text-white" data-testid="attachments-tab">
                <ImagePlus className="w-4 h-4 mr-1.5" />
                {t('addImagesTab')}
              </TabsTrigger>
            </TabsList>

            {/* Materials Tab */}
            <TabsContent value="materials" className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-slate-900">{t('materials')}</h3>
                <Button
                  onClick={addMaterial}
                  className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl"
                  data-testid="add-material-button"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  {t('addMaterial')}
                </Button>
              </div>

              {materials.length === 0 ? (
                <Card className="bg-white/50 border-2 border-dashed border-slate-300">
                  <CardContent className="py-12 text-center">
                    <p className="text-slate-600">{t('noMaterialsYet')}</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {materials.map((item, index) => {
                    const currentRoom = (item.room || '').trim();
                    const prevRoom = index > 0 ? ((materials[index - 1].room || '').trim()) : null;
                    const showRoomBanner = currentRoom && currentRoom !== prevRoom && roomHasVisible(materials, currentRoom);
                    const cardVisible = matchesSearch(item);
                    return (
                      <React.Fragment key={item.id}>
                        {showRoomBanner && (
                          <div className="flex items-center gap-2 pt-3 pb-1" data-testid={`room-banner-material-${index}`}>
                            <div className="h-px flex-1 bg-gradient-to-r from-teal-300/60 to-transparent"></div>
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold text-teal-800 bg-teal-50 border border-teal-200">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                              </svg>
                              {currentRoom}
                            </span>
                            <div className="h-px flex-1 bg-gradient-to-l from-teal-300/60 to-transparent"></div>
                          </div>
                        )}
                        <div className={cardVisible ? '' : 'hidden'} data-testid={`material-visibility-${index}`} data-item-id={item.id}>
                        <Card className={`bg-white border shadow-sm ${_q && cardVisible ? 'border-amber-300 ring-2 ring-amber-100' : 'border-slate-200'}`} data-testid={`material-item-${index}`}>
                      <CardContent className="pt-6">
                        <div className="space-y-4">
                          <div className="flex justify-between items-start">
                            <h4 className="text-lg font-semibold text-slate-900" data-testid={`material-header-${index}`}>
                              {(item.name || '').trim() || `${t('material')} #${index + 1}`}
                            </h4>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeMaterial(index)}
                              className="text-red-600 hover:bg-red-50 rounded-lg"
                              data-testid={`remove-material-${index}`}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </Button>
                          </div>

                          {/* Oda / Bölüm — hangi mekân için (Yatak Odası, Mutfak, Balkon vb.) */}
                          <div>
                            <Label>{t('roomLabel')}</Label>
                            <Input
                              data-testid={`material-room-${index}`}
                              value={item.room || ''}
                              onChange={(e) => updateMaterial(index, "room", e.target.value)}
                              placeholder={t('roomPlaceholder')}
                              list={`rooms-datalist-${index}`}
                              className="rounded-lg"
                            />
                            <datalist id={`rooms-datalist-${index}`}>
                              {Array.from(new Set([
                                ...materials.map(m => (m.room || '').trim()).filter(Boolean),
                                ...labor.map(l => (l.room || '').trim()).filter(Boolean),
                              ])).map(r => <option key={r} value={r} />)}
                            </datalist>
                          </div>

                          <div>
                            <Label>{t('materialNameLabel')}</Label>
                            <Input
                              data-testid={`material-name-${index}`}
                              value={item.name}
                              onChange={(e) => updateMaterial(index, "name", e.target.value)}
                              placeholder={t('materialNamePlaceholder')}
                              className="rounded-lg"
                            />
                          </div>

                          <div>
                            <Label>{t('descriptionLabel')}</Label>
                            <Input
                              value={item.description}
                              onChange={(e) => updateMaterial(index, "description", e.target.value)}
                              placeholder={t('descriptionPlaceholder')}
                              className="rounded-lg"
                            />
                          </div>

                          <ItemSmartFields
                            item={item}
                            index={index}
                            itemType="material"
                            lang={lang}
                            onPatch={(patch) => patchMaterial(index, patch)}
                          />

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <Label>{t('quantity')}</Label>
                              <FormattedNumberInput
                                data-testid={`material-quantity-${index}`}
                                value={item.quantity}
                                onChange={(n) => updateMaterial(index, "quantity", n)}
                                className="rounded-lg"
                              />
                            </div>
                            <div>
                              <Label>{t('unit')}</Label>
                              <Select
                                value={item.unit}
                                onValueChange={(value) => updateMaterial(index, "unit", value)}
                              >
                                <SelectTrigger className="rounded-lg">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="m²">m²</SelectItem>
                                  <SelectItem value="m³">m³</SelectItem>
                                  <SelectItem value="adet">adet</SelectItem>
                                  <SelectItem value="kg">kg</SelectItem>
                                  <SelectItem value="ton">ton</SelectItem>
                                  <SelectItem value="lt">litre</SelectItem>
                                  <SelectItem value="mtül">mtül</SelectItem>
                                  <SelectItem value="torba">torba</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label>{t('unitPrice')} (₺)</Label>
                              <FormattedNumberInput
                                data-testid={`material-price-${index}`}
                                value={item.unit_price}
                                onChange={(n) => updateMaterial(index, "unit_price", n)}
                                className="rounded-lg"
                              />
                            </div>
                            <div>
                              <Label>{t('total')} (₺)</Label>
                              <Input
                                value={formatNumber(lineTotal(item))}
                                disabled
                                className="rounded-lg bg-slate-100 font-semibold"
                                data-testid={`material-total-${index}`}
                              />
                            </div>
                          </div>

                          <div>
                            <Label>{t('imagesMultiLabel')}</Label>
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={(e) => handleImageUpload(e, "material", index)}
                              className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
                            />
                            {item.images && item.images.length > 0 && (
                              <div className="mt-3 grid grid-cols-3 gap-2">
                                {item.images.map((imgUrl, imgIdx) => (
                                  <div key={imgIdx} className="relative group">
                                    <img 
                                      src={imgUrl} 
                                      alt={`${item.name} ${imgIdx + 1}`} 
                                      className="w-full h-24 object-cover rounded-lg border border-slate-200"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => removeImage("material", index, imgIdx)}
                                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                      data-testid={`remove-material-img-${index}-${imgIdx}`}
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                        </div>
                      </React.Fragment>
                    );
                  })}
                  {_q && !anyMatchInList(materials) && (
                    <div className="text-center text-sm text-slate-500 py-4">
                      {t('noResults')}
                    </div>
                  )}
                </div>
              )}

              <Card className="bg-gradient-to-r from-teal-50 to-emerald-50 border border-teal-200">
                <CardContent className="py-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-slate-900">{t('materialTotalLabel')}</span>
                    <span className="text-2xl font-bold text-teal-600" data-testid="material-total-sum">{formatMoney(materialTotal, lang)}</span>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Labor Tab */}
            <TabsContent value="labor" className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-slate-900">{t('labor')}</h3>
                <Button
                  onClick={addLabor}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                  data-testid="add-labor-button"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  {t('addLabor')}
                </Button>
              </div>

              {labor.length === 0 ? (
                <Card className="bg-white/50 border-2 border-dashed border-slate-300">
                  <CardContent className="py-12 text-center">
                    <p className="text-slate-600">{t('noLaborYet')}</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {labor.map((item, index) => {
                    const currentRoom = (item.room || '').trim();
                    const prevRoom = index > 0 ? ((labor[index - 1].room || '').trim()) : null;
                    const showRoomBanner = currentRoom && currentRoom !== prevRoom && roomHasVisible(labor, currentRoom);
                    const cardVisible = matchesSearch(item);
                    return (
                      <React.Fragment key={item.id}>
                        {showRoomBanner && (
                          <div className="flex items-center gap-2 pt-3 pb-1" data-testid={`room-banner-labor-${index}`}>
                            <div className="h-px flex-1 bg-gradient-to-r from-emerald-300/60 to-transparent"></div>
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                              </svg>
                              {currentRoom}
                            </span>
                            <div className="h-px flex-1 bg-gradient-to-l from-emerald-300/60 to-transparent"></div>
                          </div>
                        )}
                        <div className={cardVisible ? '' : 'hidden'} data-testid={`labor-visibility-${index}`} data-item-id={item.id}>
                        <Card className={`bg-white border shadow-sm ${_q && cardVisible ? 'border-amber-300 ring-2 ring-amber-100' : 'border-slate-200'}`} data-testid={`labor-item-${index}`}>
                      <CardContent className="pt-6">
                        <div className="space-y-4">
                          <div className="flex justify-between items-start">
                            <h4 className="text-lg font-semibold text-slate-900" data-testid={`labor-header-${index}`}>
                              {(item.name || '').trim() || `${t('labor')} #${index + 1}`}
                            </h4>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeLabor(index)}
                              className="text-red-600 hover:bg-red-50 rounded-lg"
                              data-testid={`remove-labor-${index}`}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </Button>
                          </div>

                          {/* Oda / Bölüm — hangi mekân için (Yatak Odası, Mutfak, Balkon vb.) */}
                          <div>
                            <Label>{t('roomLabel')}</Label>
                            <Input
                              data-testid={`labor-room-${index}`}
                              value={item.room || ''}
                              onChange={(e) => updateLabor(index, "room", e.target.value)}
                              placeholder={t('roomPlaceholder')}
                              list={`rooms-datalist-labor-${index}`}
                              className="rounded-lg"
                            />
                            <datalist id={`rooms-datalist-labor-${index}`}>
                              {Array.from(new Set([
                                ...materials.map(m => (m.room || '').trim()).filter(Boolean),
                                ...labor.map(l => (l.room || '').trim()).filter(Boolean),
                              ])).map(r => <option key={r} value={r} />)}
                            </datalist>
                          </div>

                          <div>
                            <Label>{t('laborNameLabel')}</Label>
                            <Input
                              data-testid={`labor-name-${index}`}
                              value={item.name}
                              onChange={(e) => updateLabor(index, "name", e.target.value)}
                              placeholder={t('laborNamePlaceholder')}
                              className="rounded-lg"
                            />
                          </div>

                          <div>
                            <Label>{t('descriptionLabel')}</Label>
                            <Input
                              value={item.description}
                              onChange={(e) => updateLabor(index, "description", e.target.value)}
                              placeholder={t('descriptionPlaceholder')}
                              className="rounded-lg"
                            />
                          </div>

                          <ItemSmartFields
                            item={item}
                            index={index}
                            itemType="labor"
                            lang={lang}
                            onPatch={(patch) => patchLabor(index, patch)}
                          />

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <Label>{t('quantity')}</Label>
                              <FormattedNumberInput
                                data-testid={`labor-quantity-${index}`}
                                value={item.quantity}
                                onChange={(n) => updateLabor(index, "quantity", n)}
                                className="rounded-lg"
                              />
                            </div>
                            <div>
                              <Label>{t('unit')}</Label>
                              <Select
                                value={item.unit}
                                onValueChange={(value) => updateLabor(index, "unit", value)}
                              >
                                <SelectTrigger className="rounded-lg">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="m²">m²</SelectItem>
                                  <SelectItem value="m³">m³</SelectItem>
                                  <SelectItem value="adet">adet</SelectItem>
                                  <SelectItem value="gün">gün</SelectItem>
                                  <SelectItem value="saat">saat</SelectItem>
                                  <SelectItem value="mtül">mtül</SelectItem>
                                  <SelectItem value="lt">litre</SelectItem>
                                  <SelectItem value="torba">torba</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label>{t('unitPrice')} (₺)</Label>
                              <FormattedNumberInput
                                data-testid={`labor-price-${index}`}
                                value={item.unit_price}
                                onChange={(n) => updateLabor(index, "unit_price", n)}
                                className="rounded-lg"
                              />
                            </div>
                            <div>
                              <Label>{t('total')} (₺)</Label>
                              <Input
                                value={formatNumber(lineTotal(item))}
                                disabled
                                className="rounded-lg bg-slate-100 font-semibold"
                                data-testid={`labor-total-${index}`}
                              />
                            </div>
                          </div>

                          <div>
                            <Label>{t('imagesMultiLabel')}</Label>
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={(e) => handleImageUpload(e, "labor", index)}
                              className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
                            />
                            {item.images && item.images.length > 0 && (
                              <div className="mt-3 grid grid-cols-3 gap-2">
                                {item.images.map((imgUrl, imgIdx) => (
                                  <div key={imgIdx} className="relative group">
                                    <img 
                                      src={imgUrl} 
                                      alt={`${item.name} ${imgIdx + 1}`} 
                                      className="w-full h-24 object-cover rounded-lg border border-slate-200"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => removeImage("labor", index, imgIdx)}
                                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                      data-testid={`remove-labor-img-${index}-${imgIdx}`}
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                        </div>
                      </React.Fragment>
                    );
                  })}
                  {_q && !anyMatchInList(labor) && (
                    <div className="text-center text-sm text-slate-500 py-4">
                      {t('noResults')}
                    </div>
                  )}
                </div>
              )}

              <Card className="bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200">
                <CardContent className="py-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-slate-900">{t('laborTotalLabel')}</span>
                    <span className="text-2xl font-bold text-emerald-600" data-testid="labor-total-sum">{formatMoney(laborTotal, lang)}</span>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Attachments Tab */}
            <TabsContent value="attachments" className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <div>
                  <h3 className="text-xl font-semibold text-slate-900">{t('attachmentsTitle')}</h3>
                  <p className="text-sm text-slate-500">
                    {t('attachmentsDesc')}
                  </p>
                </div>
                <div>
                  <input
                    type="file"
                    multiple
                    id="attachment-file-input"
                    className="hidden"
                    onChange={handleAttachmentUpload}
                    data-testid="attachment-file-input"
                  />
                  <Button
                    onClick={() => document.getElementById('attachment-file-input').click()}
                    disabled={attachmentUploading}
                    className="bg-purple-600 hover:bg-purple-700 text-white rounded-xl"
                    data-testid="add-attachment-button"
                  >
                    <ImagePlus className="w-4 h-4 mr-2" />
                    {attachmentUploading ? t('uploadingBtn') : t('addAttachmentBtn')}
                  </Button>
                </div>
              </div>

              {attachments.length === 0 ? (
                <Card className="bg-white/50 border-2 border-dashed border-purple-200">
                  <CardContent className="py-10 text-center text-slate-500" data-testid="attachments-empty">
                    {t('noAttachmentsYet')}
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {attachments.map((att, index) => (
                    <Card key={att.id || index} className="bg-white border border-slate-200 shadow-sm" data-testid={`attachment-card-${index}`}>
                      <CardContent className="p-3 space-y-2">
                        <div className="relative">
                          {att.is_image && att.url ? (
                            <img
                              src={att.url}
                              alt={att.name}
                              className="w-full h-36 object-contain bg-slate-50 rounded-lg border border-slate-100"
                            />
                          ) : (
                            <div className="w-full h-36 flex flex-col items-center justify-center bg-slate-50 rounded-lg border border-slate-100 text-slate-400">
                              <FileText className="w-10 h-10 mb-1" />
                              <span className="text-xs uppercase font-semibold">{att.mime || "dosya"}</span>
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => removeAttachment(index)}
                            className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1"
                            data-testid={`remove-attachment-${index}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div>
                          <Label className="text-xs">{t('attachmentNameLabel')}</Label>
                          <Input
                            value={att.name || ""}
                            onChange={(e) => updateAttachment(index, "name", e.target.value)}
                            className="rounded-lg h-8 text-sm"
                            data-testid={`attachment-name-${index}`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">{t('attachmentInfoLabel')}</Label>
                          <Input
                            value={att.description || ""}
                            onChange={(e) => updateAttachment(index, "description", e.target.value)}
                            placeholder={t('attachmentInfoPlaceholder')}
                            className="rounded-lg h-8 text-sm"
                            data-testid={`attachment-desc-${index}`}
                          />
                        </div>
                        {att.size ? (
                          <p className="text-[11px] text-slate-400">{(att.size / 1024).toFixed(0)} KB</p>
                        ) : null}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>

          {/* KDV and Discount */}
          <Card className="bg-white/70 backdrop-blur-sm border border-slate-200 shadow-md">
            <CardHeader>
              <CardTitle className="text-xl text-slate-900">{t('kdvDiscountTitle')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="kdv-rate">{t('kdvRateLabel')}</Label>
                  <div className="flex items-center space-x-2">
                    <Input
                      id="kdv-rate"
                      data-testid="kdv-rate-input"
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={kdvRate}
                      onChange={(e) => setKdvRate(parseFloat(e.target.value) || 0)}
                      className="rounded-lg"
                      placeholder="0"
                    />
                    <div className="flex space-x-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setKdvRate(10)}
                        className="rounded-lg"
                      >
                        %10
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setKdvRate(20)}
                        className="rounded-lg"
                      >
                        %20
                      </Button>
                    </div>
                  </div>
                  {kdvRate > 0 && (
                    <p className="text-sm text-blue-600 font-medium">
                      {t('kdvAmountLabel')} {formatMoney(kdvAmount, lang)}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="discount-rate">{t('discountRateLabel')}</Label>
                  <div className="flex items-center space-x-2">
                    <Input
                      id="discount-rate"
                      data-testid="discount-rate-input"
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={discountRate}
                      onChange={(e) => setDiscountRate(parseFloat(e.target.value) || 0)}
                      className="rounded-lg"
                      placeholder="0"
                    />
                    <div className="flex space-x-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setDiscountRate(5)}
                        className="rounded-lg"
                      >
                        %5
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setDiscountRate(10)}
                        className="rounded-lg"
                      >
                        %10
                      </Button>
                    </div>
                  </div>
                  {discountRate > 0 && (
                    <p className="text-sm text-rose-600 font-medium">
                      {t('discountAmountLabel')} -{formatMoney(discountAmount, lang)}
                    </p>
                  )}
                </div>
              </div>

              <div className="pt-4 border-t border-slate-200 space-y-2">
                {profitMargin > 0 && (
                  <div className="flex justify-between text-amber-600 text-sm" data-testid="profit-margin-info">
                    <span>{t('profitIncludedTemplate').replace('{pm}', profitMargin)}</span>
                    <span className="text-xs text-slate-400">{t('invisibleToCustomer')}</span>
                  </div>
                )}
                <div className="flex justify-between text-slate-700">
                  <span className="font-medium">Ara Toplam:</span>
                  <span className="font-semibold" data-testid="subtotal">{formatMoney(subtotal, lang)}</span>
                </div>
                {kdvRate > 0 && (
                  <div className="flex justify-between text-blue-600">
                    <span>KDV (%{kdvRate}):</span>
                    <span className="font-semibold">{formatMoney(kdvAmount, lang)}</span>
                  </div>
                )}
                {discountRate > 0 && (
                  <div className="flex justify-between text-rose-600">
                    <span>{t('discountWord')} (%{discountRate}):</span>
                    <span className="font-semibold">-{formatMoney(discountAmount, lang)}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Terms & Conditions */}
          <Card className="bg-white border border-slate-200 shadow-md">
            <CardHeader>
              <CardTitle className="text-xl text-slate-900">{t('termsConditions')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="payment-terms">{t('paymentTerms')}</Label>
                <Input
                  id="payment-terms"
                  value={paymentTerms}
                  onChange={(e) => setPaymentTerms(e.target.value)}
                  placeholder={t('paymentTermsPlaceholder')}
                  className="rounded-lg"
                />
              </div>
              <div>
                <Label htmlFor="validity-period">{t('validityPeriod')}</Label>
                <Input
                  id="validity-period"
                  value={validityPeriod}
                  onChange={(e) => setValidityPeriod(e.target.value)}
                  placeholder={t('validityPlaceholder')}
                  className="rounded-lg"
                />
              </div>
              <div>
                <Label htmlFor="notes">{t('notesOtherTerms')}</Label>
                <textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t('notesPlaceholder')}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
                />
              </div>
            </CardContent>
          </Card>

          {/* Bank Information */}
          <Card className="bg-white border border-slate-200 shadow-md">
            <CardHeader>
              <CardTitle className="text-xl text-slate-900">Banka Bilgileri</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="account-holder">Hesap Sahibi</Label>
                <Input
                  id="account-holder"
                  value={accountHolder}
                  onChange={(e) => setAccountHolder(e.target.value)}
                  placeholder={t('accountHolderPlaceholder')}
                  className="rounded-lg"
                />
              </div>
              <div>
                <Label htmlFor="bank-name">{t('bankName')}</Label>
                <Input
                  id="bank-name"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  placeholder={t('bankNamePlaceholder')}
                  className="rounded-lg"
                />
              </div>
              <div>
                <Label htmlFor="iban">IBAN</Label>
                <Input
                  id="iban"
                  value={iban}
                  onChange={(e) => setIban(e.target.value)}
                  placeholder="TR00 0000 0000 0000 0000 0000 00"
                  className="rounded-lg"
                />
              </div>
            </CardContent>
          </Card>

          {/* Grand Total */}
          <Card className="bg-gradient-to-r from-amber-100 to-orange-100 border-2 border-amber-300 shadow-lg">
            <CardContent className="py-6">
              <div className="flex justify-between items-center">
                <span className="text-2xl font-bold text-slate-900">GENEL TOPLAM:</span>
                <span className="text-4xl font-bold text-amber-600" data-testid="grand-total">{formatMoney(grandTotal, lang)}</span>
              </div>
            </CardContent>
          </Card>
          </div>

          {/* Floating action buttons — sticky on right side so users don't need
              to scroll back up to add a new item when the page gets long. */}
          <div
            className="hidden md:flex fixed right-4 top-1/2 -translate-y-1/2 z-40 flex-col gap-3"
            data-testid="floating-add-buttons"
          >
            <button
              type="button"
              onClick={addMaterial}
              data-testid="floating-add-material"
              title={t('addMaterial')}
              className="group bg-teal-600 hover:bg-teal-700 text-white shadow-lg hover:shadow-xl rounded-full pl-3 pr-4 py-3 flex items-center gap-2 transition-all hover:scale-105"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              <span className="text-sm font-semibold whitespace-nowrap">{t('material')}</span>
            </button>
            <button
              type="button"
              onClick={addLabor}
              data-testid="floating-add-labor"
              title={t('addLabor')}
              className="group bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg hover:shadow-xl rounded-full pl-3 pr-4 py-3 flex items-center gap-2 transition-all hover:scale-105"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" />
              </svg>
              <span className="text-sm font-semibold whitespace-nowrap">{t('labor')}</span>
            </button>
          </div>

          {/* Mobile: smaller FAB at bottom-right (compact icons only) */}
          <div
            className="md:hidden fixed right-3 bottom-4 z-40 flex flex-col gap-2"
            data-testid="floating-add-buttons-mobile"
          >
            <button
              type="button"
              onClick={addMaterial}
              data-testid="floating-add-material-mobile"
              title={t('addMaterial')}
              className="bg-teal-600 hover:bg-teal-700 text-white shadow-lg rounded-full w-12 h-12 flex items-center justify-center"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </button>
            <button
              type="button"
              onClick={addLabor}
              data-testid="floating-add-labor-mobile"
              title={t('addLabor')}
              className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg rounded-full w-12 h-12 flex items-center justify-center"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" />
              </svg>
            </button>
          </div>

          {/* Şablon olarak kaydet dialogu */}
          <Dialog open={tplDialogOpen} onOpenChange={setTplDialogOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{t('tplDialogTitle')}</DialogTitle>
              </DialogHeader>
              <div className="space-y-2">
                <Label htmlFor="tpl-name">{t('tplNameLabel')}</Label>
                <Input
                  id="tpl-name"
                  value={tplName}
                  onChange={(e) => setTplName(e.target.value)}
                  placeholder={t('tplNamePlaceholder')}
                  data-testid="template-name-input"
                />
                <p className="text-xs text-slate-500">
                  {t('tplSaveNote').replace('{m}', materials.length).replace('{l}', labor.length)}
                </p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setTplDialogOpen(false)}>
                  {t('giveUpBtn')}
                </Button>
                <Button
                  onClick={saveTemplate}
                  disabled={tplSaving}
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                  data-testid="confirm-save-template"
                >
                  {tplSaving ? t('saving') : t('save')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </main>
      </div>
    </div>
  );
};

export default ProposalEditor;
